import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import Header from "./components/Header";
import Footer from "./components/Footer";

import hotelBbc from "../images/hotels/hotel_bbc.jpg";
import hotelHohberg from "../images/hotels/hotel_hohberg.jpg";
import hotelKronenhof from "../images/hotels/hotel_kronenhof.png";
import hotelVienna from "../images/hotels/hotel_vienna.jpg";

const Accommodation = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resources, setResources] = useState({});
    const [errors, setErrors] = useState("");
    const [formSettings, setFormSettings] = useState(null);
    const [ticketData, setTicketData] = useState({});
    const [roomSelections, setRoomSelections] = useState({});
    const [extraBookings, setExtraBookings] = useState({});
    const [expandedHotels, setExpandedHotels] = useState({});

    const navigate = useNavigate();
    const baseUrl = process.env.REACT_APP_BASEURL_API;
    const { language } = useParams();
    const languageId = { de: 1, fr: 2, en: 3, it: 4 }[language] ?? 1;

    useEffect(() => {
        const orderId = sessionStorage.getItem("orderId");
        if (!orderId) {
            navigate(`/${language}/participant`);
            return;
        }
        const token = sessionStorage.getItem("token");
        if (token) {
            axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        }
        requestResources();
        loadSettingsAndTickets();
    }, []);

    useEffect(() => {
        requestResources();
    }, [language]);

    const requestResources = async () => {
        try {
            const res = await axios.get(`form/resources/${language}`);
            setResources(res.data?.translation);
        } catch (error) {
            console.error(error);
        }
    };

    const loadSettingsAndTickets = async () => {
        try {
            const res = await axios.get("form/formsettings");
            const settings = res.data;
            setFormSettings(settings);

            const orderId = sessionStorage.getItem("orderId");
            try {
                await axios.get(`${baseUrl}/ShopBasket/Order/${orderId}`);
            } catch {
                sessionStorage.removeItem("orderId");
                navigate(`/${language}/participant`);
                return;
            }

            await loadAllTicketData(settings, orderId);
        } catch (error) {
            console.error(error);
        }
    };

    const getAccommodationIds = (settings) => [
        settings.bbcArenaDoppelzimmerTicketTypeId,
        settings.bbcArena4BettZimmerTicketTypeId,
        settings.hotelHohbergEinzelzimmerGrandLitTicketTypeId,
        settings.hotelHohbergEinzelzimmerComfortTicketTypeId,
        settings.hotelHohbergDzSuperiorTicketTypeId,
        settings.hotelHohbergDzComfortTicketTypeId,
        settings.hotelHohbergDzBudgetTicketTypeId,
        settings.hotelHohberg3BettZimmerTicketTypeId,
        settings.hotelHohberg4BettZimmerTicketTypeId,
        settings.hotelHohberg6BettZimmerTicketTypeId,
        settings.hotelKronenhofEinzelzimmerTicketTypeId,
        settings.hotelKronenhofDzEconomyGrandLitTicketTypeId,
        settings.hotelKronenhofDzStandardTicketTypeId,
        settings.hotelKronenhofDzMunotTicketTypeId,
        settings.hotelKronenhofDzSuperiorTicketTypeId,
        settings.hotelKronenhofSuiteGrandLitTicketTypeId,
        settings.hotelViennaDoppelzimmerTicketTypeId,
    ].filter(id => id && id > 0);

    const loadAllTicketData = async (settings, orderId) => {
        const ids = getAccommodationIds(settings);
        const results = {};
        await Promise.all(ids.map(async (id) => {
            try {
                const [typeRes, availRes, infosRes] = await Promise.all([
                    axios.get(`${baseUrl}/TicketType/${id}`),
                    axios.get(`${baseUrl}/TicketType/${id}/Availability?orderId=${orderId}`),
                    axios.get(`${baseUrl}/TicketType/${id}/Infos`)
                ]);
                const infos = infosRes.data.ticketTypeInfos ?? [];
                const info = infos.find(i => i.languageId === languageId)
                    ?? infos.find(i => i.languageId === 1)
                    ?? infos[0];
                results[id] = {
                    price: typeRes.data.priceInclServiceFee ?? typeRes.data.price ?? 0,
                    availability: availRes.data,
                    remainingCapacity: availRes.data.remainingCapacity ?? availRes.data.availableTickets ?? 10,
                    name: info?.name ?? "",
                };
            } catch (err) {
                console.error(`Error loading ticket type ${id}:`, err);
                results[id] = { price: null, availability: { status: 1 }, name: "" };
            }
        }));
        setTicketData(results);

        const initial = {};
        ids.forEach(id => { initial[id] = { quantity: 0, anreise: "", abreise: "" }; });
        setRoomSelections(initial);
    };

    const getName = (id) => {
        const name = ticketData[id]?.name ?? "";
        const colonIndex = name.indexOf(": ");
        return colonIndex !== -1 ? name.slice(colonIndex + 2) : name;
    };

    const getPrice = (id) => {
        const data = ticketData[id];
        if (!data || data.price === null) return null;
        return `CHF ${Number(data.price).toFixed(2)}`;
    };

    const isSoldOut = (id) => {
        const data = ticketData[id];
        return data && data.availability && data.availability.status !== 0;
    };

    const handleLanguageChange = (lang) => {
        navigate(`/${lang}/accommodation`);
    };

    const updateRoom = (id, field, value) => {
        setRoomSelections(prev => {
            const current = prev[id];
            const updated = { ...current, [field]: value };
            if (field === "anreise" && updated.abreise && updated.abreise <= value) {
                updated.abreise = "";
            }
            return { ...prev, [id]: updated };
        });
    };

    const getRemainingCapacity = (id) => {
        const total = ticketData[id]?.remainingCapacity ?? 10;
        const mainQty = roomSelections[id]?.quantity ?? 0;
        const extraQty = (extraBookings[id] || []).reduce((sum, e) => sum + (e.quantity ?? 0), 0);
        return Math.max(0, total - mainQty - extraQty);
    };

    const addExtraBooking = (id) => {
        setExtraBookings(prev => ({
            ...prev,
            [id]: [...(prev[id] || []), { quantity: 0, anreise: "", abreise: "" }]
        }));
    };

    const updateExtraBooking = (id, index, field, value) => {
        setExtraBookings(prev => {
            const updated = [...(prev[id] || [])];
            const entry = { ...updated[index], [field]: value };
            if (field === "anreise" && entry.abreise && entry.abreise <= value) {
                entry.abreise = "";
            }
            updated[index] = entry;
            return { ...prev, [id]: updated };
        });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}.${month}.${year}`;
    };

    const buildOrderOptions = (settings) => {
        const optionMap = [
            { id: settings.bbcArenaDoppelzimmerTicketTypeId, option: "option2" },
            { id: settings.bbcArena4BettZimmerTicketTypeId, option: "option3" },
            { id: settings.hotelHohbergEinzelzimmerGrandLitTicketTypeId, option: "option4" },
            { id: settings.hotelHohbergEinzelzimmerComfortTicketTypeId, option: "option5" },
            { id: settings.hotelHohbergDzSuperiorTicketTypeId, option: "option6" },
            { id: settings.hotelHohbergDzComfortTicketTypeId, option: "option7" },
            { id: settings.hotelHohbergDzBudgetTicketTypeId, option: "option8" },
            { id: settings.hotelHohberg3BettZimmerTicketTypeId, option: "option9" },
            { id: settings.hotelHohberg4BettZimmerTicketTypeId, option: "option10" },
            { id: settings.hotelHohberg6BettZimmerTicketTypeId, option: "option11" },
            { id: settings.hotelKronenhofEinzelzimmerTicketTypeId, option: "option12" },
            { id: settings.hotelKronenhofDzEconomyGrandLitTicketTypeId, option: "option13" },
            { id: settings.hotelKronenhofDzStandardTicketTypeId, option: "option14" },
            { id: settings.hotelKronenhofDzMunotTicketTypeId, option: "option15" },
            { id: settings.hotelKronenhofDzSuperiorTicketTypeId, option: "option16" },
            { id: settings.hotelKronenhofSuiteGrandLitTicketTypeId, option: "option17" },
            { id: settings.hotelViennaDoppelzimmerTicketTypeId, option: "option18" },
        ];

        const options = {};
        optionMap.forEach(({ id, option }) => {
            const sel = roomSelections[id];
            let value = "";
            if (sel && sel.quantity > 0) {
                value = `Hotelzimmer: ${getName(id)}, Anzahl Zimmer: ${sel.quantity}, Anreise: ${formatDate(sel.anreise)}, Abreise: ${formatDate(sel.abreise)}`;
            }
            (extraBookings[id] || []).forEach(extra => {
                if (!extra.quantity) return;
                const part = `Hotelzimmer: ${getName(id)}, Anzahl Zimmer: ${extra.quantity}, Anreise: ${formatDate(extra.anreise)}, Abreise: ${formatDate(extra.abreise)}`;
                value = value ? value + ", " + part : part;
            });
            options[option] = value;
        });
        return options;
    };

    const calcNights = (anreise, abreise) => {
        if (!anreise || !abreise) return 1;
        const diff = new Date(abreise) - new Date(anreise);
        const nights = Math.round(diff / (1000 * 60 * 60 * 24));
        return nights > 0 ? nights : 1;
    };

    const buildAccommodationTickets = (buyerInfo) => {
        const tickets = [];
        const makeTicket = (ticketTypeId) => ({
            ticketTypeId,
            quantity: 1,
            salutation: buyerInfo?.salutation,
            firstname: buyerInfo?.firstName,
            lastname: buyerInfo?.name,
            company: buyerInfo?.company,
            email: buyerInfo?.email,
            phone: buyerInfo?.telephone,
            street: (buyerInfo?.street || "") + " " + (buyerInfo?.streetNumber || ""),
            city: buyerInfo?.city,
            postCode: buyerInfo?.postCode,
            countryId: buyerInfo?.countryId || 176,
        });
        Object.entries(roomSelections).forEach(([idStr, sel]) => {
            const id = parseInt(idStr);
            const nights = calcNights(sel.anreise, sel.abreise);
            for (let i = 0; i < sel.quantity * nights; i++) tickets.push(makeTicket(id));
            (extraBookings[id] || []).forEach(extra => {
                const extraNights = calcNights(extra.anreise, extra.abreise);
                for (let i = 0; i < (extra.quantity ?? 0) * extraNights; i++) tickets.push(makeTicket(id));
            });
        });
        return tickets;
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const currentOrderId = sessionStorage.getItem("orderId");
            try {
                await axios.get(`${baseUrl}/ShopBasket/Order/${currentOrderId}`);
            } catch {
                sessionStorage.removeItem("orderId");
                navigate(`/${language}/participant`);
                return;
            }

            const buyerInfo = JSON.parse(sessionStorage.getItem("buyerInfo") || "null");
            const mainBuyerInfo = JSON.parse(sessionStorage.getItem("mainBuyerInfo") || null) ?? buyerInfo;

            const pending = JSON.parse(sessionStorage.getItem("pendingTickets") || "[]");
            const programTickets = pending.filter(Boolean).flat();
            const accommodationTickets = buildAccommodationTickets(buyerInfo);
            const allTickets = [...programTickets, ...accommodationTickets];

            await axios.post(`${baseUrl}/ShopBasket/Order/${currentOrderId}/Tickets`, { ticketsToAdd: allTickets });

            const addressBody = {
                salutation: mainBuyerInfo?.salutation,
                firstName: mainBuyerInfo?.firstName,
                name: mainBuyerInfo?.name,
                company: mainBuyerInfo?.company,
                function: mainBuyerInfo?.function,
                street: (mainBuyerInfo?.street || "") + (mainBuyerInfo?.streetNumber ? " " + mainBuyerInfo.streetNumber : ""),
                postCode: mainBuyerInfo?.postCode,
                city: mainBuyerInfo?.city,
                email: mainBuyerInfo?.email,
                countryId: mainBuyerInfo?.countryId || 176,
                languageId: languageId
            };
            await axios.put(`${baseUrl}/Order/${currentOrderId}/Address`, addressBody);

            // save hotels in order options (POST to create if not yet existing, PUT to update otherwise)
            const orderOptions = buildOrderOptions(formSettings);
            try {
                await axios.get(`${baseUrl}/Order/${currentOrderId}/Options`);
                await axios.put(`${baseUrl}/Order/${currentOrderId}/Options`, orderOptions);
            } catch {
                await axios.post(`${baseUrl}/Order/${currentOrderId}/Options`, orderOptions);
            }

            navigate(`/${language}/checkout`);
        } catch (error) {
            console.error("Error during accommodation submission:", error);
            setErrors(resources?.ErrorOccurred);
            setIsSubmitting(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    const hotelMinDate = new Date(2027, 8, 30);
    const hotelMaxDate = new Date(2027, 9, 3);

    const toDateObj = (dateStr) => {
        if (!dateStr) return null;
        const [y, m, d] = dateStr.split("-").map(Number);
        return new Date(y, m - 1, d);
    };
    const toDateStr = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const nextDayDate = (dateStr) => {
        if (!dateStr) return hotelMinDate;
        const [y, m, day] = dateStr.split("-").map(Number);
        return new Date(y, m - 1, day + 1);
    };

    const availableDates = [
        new Date(2027, 8, 30),
        new Date(2027, 9, 1),
        new Date(2027, 9, 2),
        new Date(2027, 9, 3),
    ];

    const renderDateInput = (value, onChange, disabled, minDate) => (
        <DatePicker
            selected={toDateObj(value)}
            onChange={(date) => onChange(toDateStr(date))}
            disabled={disabled}
            minDate={minDate ?? hotelMinDate}
            maxDate={hotelMaxDate}
            highlightDates={availableDates}
            dateFormat="dd.MM.yyyy"
            openToDate={hotelMinDate}
            className="form-control form-control-sm form-hotel"
            wrapperClassName="w-100"
            placeholderText={resources?.AccommodationDatePlaceholder}
            showMonthDropdown={false}
            showYearDropdown={false}
        />
    );

    const renderHotelSection = (hotelName, hotelSubtitle, roomIds, hotelImage = null) => {
        if (!formSettings || Object.keys(ticketData).length === 0) return null;
        const isExpanded = !!expandedHotels[hotelName];
        const toggle = () => setExpandedHotels(prev => ({ ...prev, [hotelName]: !prev[hotelName] }));
        return (
            <div className="mb-5">
                <h4 className="program-day-title">{hotelName}</h4>
                <hr />
                {hotelImage && (
                    <img src={hotelImage} alt={hotelName} className="w-100 mb-2" style={{ maxHeight: "220px", objectFit: "cover" }} />
                )}
                <div className="hotel-toggle-row mt-1 mb-4" onClick={toggle}>
                    {hotelSubtitle && <span className="small">{hotelSubtitle}</span>}
                    <span className={`hotel-toggle-arrow${isExpanded ? "" : " open"}`} />
                </div>
                {isExpanded && <>
                    {/* Desktop table */}
                    <table className="table table-borderless d-none d-md-table">
                        <thead>
                            <tr>
                                <th className="align-top"></th>
                                <th className="text-center align-top" style={{ maxWidth: "190px" }}>{resources?.AccommodationPricePerRoomPerNight}</th>
                                <th className="text-center align-top" style={{ maxWidth: "80px" }}>{resources?.AccommodationRoomCount}</th>
                                <th className="text-center align-top" style={{ width: "150px" }}>{resources?.BuyerAnreise}</th>
                                <th className="text-center align-top" style={{ width: "150px" }}>{resources?.AccommodationAbreise}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roomIds.map((id) => {
                                const sel = roomSelections[id] || { quantity: 0, anreise: "", abreise: "" };
                                const soldOut = isSoldOut(id);
                                const lastExtra = (extraBookings[id] || []).slice(-1)[0];
                                const lastExtraComplete = !lastExtra || (lastExtra.quantity > 0 && lastExtra.anreise && lastExtra.abreise);
                                const showExtraButton = sel.quantity > 0 && sel.anreise && sel.abreise && getRemainingCapacity(id) > 0 && lastExtraComplete;
                                return (
                                    <React.Fragment key={id}>
                                        <tr>
                                            <td>{getName(id)}{soldOut && <span className="text-danger ms-2">({resources?.SoldOut})</span>}</td>
                                            <td className="text-center">{getPrice(id)}</td>
                                            <td className="text-center">
                                                <select className="form-select form-select-sm form-hotel" style={{ width: "75px", display: "inline-block" }} value={sel.quantity} onChange={(e) => updateRoom(id, "quantity", parseInt(e.target.value))} disabled={soldOut}>
                                                    {Array.from({ length: (ticketData[id]?.remainingCapacity || 10) + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
                                                </select>
                                            </td>
                                            <td className="text-center">{renderDateInput(sel.anreise, (v) => updateRoom(id, "anreise", v), soldOut || sel.quantity === 0, hotelMinDate)}</td>
                                            <td className="text-center">{renderDateInput(sel.abreise, (v) => updateRoom(id, "abreise", v), soldOut || sel.quantity === 0, nextDayDate(sel.anreise))}</td>
                                        </tr>
                                        {(extraBookings[id] || []).map((extra, idx) => {
                                            const maxExtra = (extra.quantity ?? 0) + getRemainingCapacity(id);
                                            return (
                                                <tr key={`extra-${id}-${idx}`}>
                                                    <td>{getName(id)}</td>
                                                    <td className="text-center">{getPrice(id)}</td>
                                                    <td className="text-center">
                                                        <select className="form-select form-select-sm form-hotel" style={{ width: "75px", display: "inline-block" }} value={extra.quantity ?? 0} onChange={(e) => updateExtraBooking(id, idx, "quantity", parseInt(e.target.value))}>
                                                            {Array.from({ length: maxExtra + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
                                                        </select>
                                                    </td>
                                                    <td className="text-center">{renderDateInput(extra.anreise, (v) => updateExtraBooking(id, idx, "anreise", v), false, hotelMinDate)}</td>
                                                    <td className="text-center">{renderDateInput(extra.abreise, (v) => updateExtraBooking(id, idx, "abreise", v), false, nextDayDate(extra.anreise))}</td>
                                                </tr>
                                            );
                                        })}
                                        {showExtraButton && (
                                            <tr><td colSpan={5}>
                                                <button type="button" className="btn btn-custom-transparent btn-sm" onClick={() => addExtraBooking(id)}>
                                                    {resources?.AccommodationAddRoomButton}
                                                </button>
                                            </td></tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Mobile cards */}
                    <div className="d-md-none">
                        {roomIds.map((id) => {
                            const sel = roomSelections[id] || { quantity: 0, anreise: "", abreise: "" };
                            const soldOut = isSoldOut(id);
                            const lastExtra = (extraBookings[id] || []).slice(-1)[0];
                            const lastExtraComplete = !lastExtra || (lastExtra.quantity > 0 && lastExtra.anreise && lastExtra.abreise);
                            const showExtraButton = sel.quantity > 0 && sel.anreise && sel.abreise && getRemainingCapacity(id) > 0 && lastExtraComplete;

                            const renderCard = (name, price, quantityEl, anreiseEl, abreiseEl) => (
                                <div className="border rounded p-3 mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong>{name}</strong>
                                        <span>{price}</span>
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-12">
                                            <label className="form-label small mb-1">{resources?.AccommodationRoomCount}</label>
                                            {quantityEl}
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small mb-1">{resources?.BuyerAnreise}</label>
                                            {anreiseEl}
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small mb-1">{resources?.AccommodationAbreise}</label>
                                            {abreiseEl}
                                        </div>
                                    </div>
                                </div>
                            );

                            return (
                                <React.Fragment key={id}>
                                    {renderCard(
                                        <>{getName(id)}{soldOut && <span className="text-danger ms-2">({resources?.SoldOut})</span>}</>,
                                        getPrice(id),
                                        <select className="form-select form-select-sm" value={sel.quantity} onChange={(e) => updateRoom(id, "quantity", parseInt(e.target.value))} disabled={soldOut}>
                                            {Array.from({ length: (ticketData[id]?.remainingCapacity || 10) + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
                                        </select>,
                                        renderDateInput(sel.anreise, (v) => updateRoom(id, "anreise", v), soldOut || sel.quantity === 0, hotelMinDate),
                                        renderDateInput(sel.abreise, (v) => updateRoom(id, "abreise", v), soldOut || sel.quantity === 0, nextDayDate(sel.anreise))
                                    )}
                                    {(extraBookings[id] || []).map((extra, idx) => {
                                        const maxExtra = (extra.quantity ?? 0) + getRemainingCapacity(id);
                                        return (
                                            <React.Fragment key={`extra-${id}-${idx}`}>
                                                {renderCard(
                                                    getName(id),
                                                    getPrice(id),
                                                    <select className="form-select form-select-sm" value={extra.quantity ?? 0} onChange={(e) => updateExtraBooking(id, idx, "quantity", parseInt(e.target.value))}>
                                                        {Array.from({ length: maxExtra + 1 }, (_, n) => <option key={n} value={n}>{n}</option>)}
                                                    </select>,
                                                    renderDateInput(extra.anreise, (v) => updateExtraBooking(id, idx, "anreise", v), false, hotelMinDate),
                                                    renderDateInput(extra.abreise, (v) => updateExtraBooking(id, idx, "abreise", v), false, nextDayDate(extra.anreise))
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {showExtraButton && (
                                        <div className="mb-3">
                                            <button type="button" className="btn btn-custom-transparent btn-sm" onClick={() => addExtraBooking(id)}>
                                                {resources?.AccommodationAddRoomButton}
                                            </button>
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </>}
            </div>
        );
    };

    const renderSummary = () => {
        if (!formSettings || Object.keys(roomSelections).length === 0) return null;

        const hotelGroups = [
            { name: resources?.AccommodationHotelBbcName, ids: [formSettings.bbcArenaDoppelzimmerTicketTypeId, formSettings.bbcArena4BettZimmerTicketTypeId] },
            { name: resources?.AccommodationHotelHohbergName, ids: [formSettings.hotelHohbergEinzelzimmerGrandLitTicketTypeId, formSettings.hotelHohbergEinzelzimmerComfortTicketTypeId, formSettings.hotelHohbergDzSuperiorTicketTypeId, formSettings.hotelHohbergDzComfortTicketTypeId, formSettings.hotelHohbergDzBudgetTicketTypeId, formSettings.hotelHohberg3BettZimmerTicketTypeId, formSettings.hotelHohberg4BettZimmerTicketTypeId, formSettings.hotelHohberg6BettZimmerTicketTypeId] },
            { name: resources?.AccommodationHotelKronenhofName, ids: [formSettings.hotelKronenhofEinzelzimmerTicketTypeId, formSettings.hotelKronenhofDzEconomyGrandLitTicketTypeId, formSettings.hotelKronenhofDzStandardTicketTypeId, formSettings.hotelKronenhofDzMunotTicketTypeId, formSettings.hotelKronenhofDzSuperiorTicketTypeId, formSettings.hotelKronenhofSuiteGrandLitTicketTypeId] },
            { name: resources?.AccommodationHotelViennaName, ids: [formSettings.hotelViennaDoppelzimmerTicketTypeId] },
        ];

        let gesamttotal = 0;

        const activeGroups = hotelGroups.map(hotel => {
            const entries = [];
            hotel.ids.forEach(id => {
                const sel = roomSelections[id];
                if (sel && sel.quantity > 0) {
                    entries.push({ name: getName(id), price: ticketData[id]?.price ?? 0, quantity: sel.quantity, anreise: sel.anreise, abreise: sel.abreise });
                }
                (extraBookings[id] || []).forEach(extra => {
                    if (extra.quantity > 0) {
                        entries.push({ name: getName(id), price: ticketData[id]?.price ?? 0, quantity: extra.quantity, anreise: extra.anreise, abreise: extra.abreise });
                    }
                });
            });
            return { ...hotel, entries };
        }).filter(h => h.entries.length > 0);

        if (activeGroups.length === 0) return null;

        activeGroups.forEach(h => h.entries.forEach(e => { gesamttotal += e.price * e.quantity * calcNights(e.anreise, e.abreise); }));

        return (
            <div className="mt-5">
                <h4 className="fw-bold mb-3">{resources?.AccommodationSummaryTitle}</h4>
                <div className="summary-box">
                    {/* Desktop header */}
                    <div className="summary-box-inner d-none d-md-block">
                        <div className="row">
                            <div className="col-5"></div>
                            <div className="col-3 text-center fw-bold small">{resources?.AccommodationPricePerRoomPerNight}</div>
                            <div className="col-2 text-center fw-bold small">{resources?.AccommodationQuantityTimesNights}</div>
                            <div className="col-2 text-center fw-bold small">{resources?.AccommodationColumnTotal}</div>
                        </div>
                    </div>

                    {activeGroups.map((hotel, hi) => {
                        const dateGroups = [];
                        hotel.entries.forEach(e => {
                            const key = `${e.anreise}|${e.abreise}`;
                            let dg = dateGroups.find(g => g.key === key);
                            if (!dg) { dg = { key, anreise: e.anreise, abreise: e.abreise, rows: [] }; dateGroups.push(dg); }
                            dg.rows.push(e);
                        });

                        return (
                            <div key={hi}>
                                <div className="fw-bold mb-1" style={{ fontSize: "1.1rem" }}>{hotel.name}</div>
                                {dateGroups.map((dg, di) => (
                                    <div key={di}>
                                        <div className="small mb-2">
                                            <span className="me-3">{resources?.BuyerAnreise}: <strong>{formatDate(dg.anreise)}</strong></span>
                                            <span>{resources?.AccommodationAbreise}: <strong>{formatDate(dg.abreise)}</strong></span>
                                        </div>
                                        {dg.rows.map((row, ri) => {
                                            const nights = calcNights(row.anreise, row.abreise);
                                            return (
                                                <React.Fragment key={ri}>
                                                    {/* Desktop row */}
                                                    <div className="row mb-1 d-none d-md-flex">
                                                        <div className="col-5">{row.name}</div>
                                                        <div className="col-3 text-center">CHF {Number(row.price).toFixed(2)}</div>
                                                        <div className="col-2 text-center">{row.quantity}x {nights} {nights !== 1 ? resources?.AccommodationNights : resources?.AccommodationNight}</div>
                                                        <div className="col-2 text-center">CHF {(row.price * row.quantity * nights).toFixed(2)}</div>
                                                    </div>
                                                    {/* Mobile row */}
                                                    <div className="d-md-none mb-2 pb-2">
                                                        <div className="fw-semibold">{row.name}</div>
                                                        <div className="d-flex justify-content-between small mt-1">
                                                            <span>CHF {Number(row.price).toFixed(2)} / {resources?.AccommodationNight}</span>
                                                            <span>{row.quantity}x {nights} {nights !== 1 ? resources?.AccommodationNights : resources?.AccommodationNight}</span>
                                                            <span className="fw-bold">CHF {(row.price * row.quantity * nights).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                ))}
                                {hi < activeGroups.length - 1 && <hr />}
                            </div>
                        );
                    })}

                    <hr />
                    <div className="d-flex justify-content-end fw-bold">
                        <span className="me-3">{resources?.AccommodationGrandTotal}</span>
                        <span>CHF {gesamttotal.toFixed(2)}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div>
            <Header resources={resources} title={resources?.HotelTitle} language={language} onLanguageChange={handleLanguageChange}
            containerClass="page-container-medium" />
            <>
                <div className="container page-container">
                    <div className="mt-4 page-container-medium">

                        {errors !== "" && (
                            <div className="alert alert-danger" role="alert">
                                {errors}
                            </div>
                        )}

                        {formSettings && renderHotelSection(
                            resources?.AccommodationHotelBbcName,
                            resources?.AccommodationHotelBbcSubtitle,
                            [
                                formSettings.bbcArenaDoppelzimmerTicketTypeId,
                                formSettings.bbcArena4BettZimmerTicketTypeId,
                            ],
                            hotelBbc
                        )}

                        {formSettings && renderHotelSection(
                            resources?.AccommodationHotelHohbergName,
                            resources?.AccommodationHotelHohbergSubtitle,
                            [
                                formSettings.hotelHohbergEinzelzimmerGrandLitTicketTypeId,
                                formSettings.hotelHohbergEinzelzimmerComfortTicketTypeId,
                                formSettings.hotelHohbergDzSuperiorTicketTypeId,
                                formSettings.hotelHohbergDzComfortTicketTypeId,
                                formSettings.hotelHohbergDzBudgetTicketTypeId,
                                formSettings.hotelHohberg3BettZimmerTicketTypeId,
                                formSettings.hotelHohberg4BettZimmerTicketTypeId,
                                formSettings.hotelHohberg6BettZimmerTicketTypeId,
                            ],
                            hotelHohberg
                        )}

                        {formSettings && renderHotelSection(
                            resources?.AccommodationHotelKronenhofName,
                            resources?.AccommodationHotelKronenhofSubtitle,
                            [
                                formSettings.hotelKronenhofEinzelzimmerTicketTypeId,
                                formSettings.hotelKronenhofDzEconomyGrandLitTicketTypeId,
                                formSettings.hotelKronenhofDzStandardTicketTypeId,
                                formSettings.hotelKronenhofDzMunotTicketTypeId,
                                formSettings.hotelKronenhofDzSuperiorTicketTypeId,
                                formSettings.hotelKronenhofSuiteGrandLitTicketTypeId,
                            ],
                            hotelKronenhof
                        )}

                        {formSettings && renderHotelSection(
                            resources?.AccommodationHotelViennaName,
                            resources?.AccommodationHotelViennaSubtitle,
                            [
                                formSettings.hotelViennaDoppelzimmerTicketTypeId,
                            ],
                            hotelVienna
                        )}

                        {renderSummary()}

                        <div className="row mt-5">
                            <div className="col-12 d-flex justify-content-between">
                                <button
                                    className="btn btn-custom"
                                    onClick={() => navigate(`/${language}/participant`)}
                                    disabled={isSubmitting}
                                >
                                    {resources?._Back}
                                </button>
                                <button
                                    className="btn btn-custom"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                >
                                    {resources?._Next}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
                <Footer language={language} />
            </>
        </div>
    );
};

export default Accommodation;
